import React, { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { pageVariants } from '../../utils/animations';
import { SimpleCounter, RatingCounter } from '../ui/AnimatedCounter';
import Skeleton from '../ui/Skeleton';
import PullToRefreshIndicator from '../ui/PullToRefreshIndicator';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import { useWebsite } from '../../contexts/WebsiteContext';
import ScrollPreviewImage from '../ScrollPreviewImage';
import { Empty, Eyebrow, HairlineGrid, PageHeader, Pill, Tag, type TagTone } from '../ui/kit';
import SetupProgressStrip from './SetupProgressStrip';
import { useSetupStatus } from '../../hooks/useSetupStatus';
import { STAGE_DEFS, isRenderableSetupStatus, isSetupComplete } from '../../lib/setupFlow';
import { MARKS } from '../../lib/marks';
import { generateReport } from '../../utils/generateReport';
import type { ReportAnalytics, ReportLead, ReportReview } from '../../utils/generateReport';
import { personalFirstName } from '../../lib/contactName';

interface Website {
  id: string;
  domain: string;
  netlifyUrl?: string;
  currentActiveUrl?: string;
  websiteStatus?: 'temporary' | 'transitioning' | 'live';
  screenshot?: string;
  status: 'active' | 'setting_up' | 'suspended';
  contentSetupComplete?: boolean;
  businessName?: string;
  trade?: string;
  createdAt: string;
  lastUpdated: string;
  leadsCount?: number;
  newLeadsCount?: number;
  reviewsCount?: number;
  avgRating?: number;
  umamiWebsiteId?: string;
}

interface Review {
  id: string;
  platform: string;
  rating: number;
  reviewerName: string;
  reviewText: string;
  responded: boolean;
  reviewDate: string;
}

interface Lead {
  id: string;
  name: string;
  service?: string;
  status: string;
  createdAt: string;
}

const LeadInitialAvatar: React.FC<{ name: string }> = ({ name }) => {
  const initial = name.trim().charAt(0).toUpperCase() || '?';

  return (
    <div
      role="img"
      aria-label={`${name || 'Lead'} initial`}
      className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-paper-deep ring-1 ring-zinc-900/[0.08]"
    >
      <span aria-hidden="true" className="font-mono text-[12px] font-medium text-zinc-700">
        {initial}
      </span>
    </div>
  );
};

/** "3 days ago" — the hero's only timestamp, so a full date would be noise. */
const formatUpdated = (iso?: string): string | null => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * The capture, framed as a small browser and centred on its own stage.
 *
 * The frame is what makes the shot read as "your website" rather than a stray
 * image, and the fixed-ratio viewport is what stops the crop drifting: as a
 * plain flex child the preview was stretched to whatever height the stats
 * column happened to be, so the same screenshot sat differently on every
 * account.
 */
const SitePreviewFrame: React.FC<{
  domain: string;
  screenshot?: string;
  alt: string;
  liveUrl: string;
  onRefreshShot: () => Promise<void>;
}> = ({ domain, screenshot, alt, liveUrl, onRefreshShot }) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await onRefreshShot();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex items-center justify-center border-b border-zinc-200 bg-paper-deep p-5 sm:p-6 lg:w-[44%] lg:border-b-0 lg:border-r">
      <div className="w-full max-w-[380px] overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
        {/* Chrome */}
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 border-b border-zinc-200 bg-paper-tint px-3 py-2">
          <div className="flex gap-1.5" aria-hidden>
            <span className="h-2 w-2 rounded-full bg-zinc-300" />
            <span className="h-2 w-2 rounded-full bg-zinc-300" />
            <span className="h-2 w-2 rounded-full bg-zinc-300" />
          </div>
          <div className="mx-auto flex min-w-0 max-w-full items-center gap-1.5 rounded-full bg-white px-2.5 py-1 ring-1 ring-zinc-900/[0.06]">
            <LockIcon className="h-3 w-3 shrink-0 text-zinc-400" />
            <span className="truncate font-mono text-[11px] text-zinc-500">{domain}</span>
          </div>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-900/[0.04] hover:text-zinc-600 disabled:opacity-60"
              title="Refresh screenshot"
              aria-label="Refresh screenshot"
            >
              <svg
                className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={1.75}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182M2.985 19.644l3.181-3.183" />
              </svg>
            </button>
            <a
              href={liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-900/[0.04] hover:text-zinc-600"
              title="Open live site"
              aria-label="Open live site"
            >
              <ExternalLinkIcon className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>

        {/* Viewport — fixed ratio, so the crop is the same on every account */}
        <div className="relative aspect-[16/10] overflow-hidden bg-paper-deep">
          {screenshot ? (
            <ScrollPreviewImage src={screenshot} alt={alt} className="h-full w-full" hint={false} />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <GlobeIcon className="h-10 w-10 text-zinc-300" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Skeleton Loading Component
const DashboardSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Header Skeleton */}
    <div className="flex items-start justify-between">
      <div>
        <Skeleton variant="text" width={200} height={28} className="mb-2" />
        <Skeleton variant="text" width={280} height={16} />
      </div>
      <Skeleton variant="rounded" width={100} height={40} />
    </div>

    {/* Hero Card Skeleton */}
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
      <div className="flex flex-col lg:flex-row">
        <div className="lg:w-2/5">
          <Skeleton variant="rectangular" className="w-full h-48 lg:h-64" />
        </div>
        <div className="lg:w-3/5 p-6 lg:p-8">
          <Skeleton variant="text" width="60%" height={28} className="mb-2" />
          <Skeleton variant="text" width={150} height={16} className="mb-6" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="p-3 bg-zinc-50 rounded-xl">
                <Skeleton variant="text" width={40} height={28} className="mb-1" />
                <Skeleton variant="text" width={60} height={14} />
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <Skeleton variant="rounded" width={120} height={40} />
            <Skeleton variant="rounded" width={100} height={40} />
            <Skeleton variant="rounded" width={80} height={40} />
          </div>
        </div>
      </div>
    </div>

    {/* Activity Cards Skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {[1, 2].map((i) => (
        <div key={i} className="bg-white border border-zinc-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <Skeleton variant="text" width={120} height={20} />
            <Skeleton variant="text" width={60} height={14} />
          </div>
          <div className="space-y-3">
            {[1, 2].map((j) => (
              <div key={j} className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                <Skeleton variant="circular" width={40} height={40} />
                <div className="flex-1">
                  <Skeleton variant="text" width="60%" height={16} className="mb-1" />
                  <Skeleton variant="text" width="40%" height={12} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const DashboardHome: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // The chrome renders this same page at /demo (fixture data, no backend) —
  // that route tree has no websites/:id/edit, /setup, or account pages, so
  // CTAs pointing there get swapped for a disabled pill instead of a link
  // that bounces back to /demo.
  const isDemo = location.pathname === '/demo' || location.pathname.startsWith('/demo/');
  const { websites, isLoading, selectedWebsiteId, setSelectedWebsiteId, selectedWebsite, isSingleWebsite, isAllSelected } = useWebsite();
  const queryClient = useQueryClient();
  const refetch = useCallback(() => queryClient.invalidateQueries({ queryKey: ['websites'] }), [queryClient]);
  const error = null; // errors handled by context

  // Same payload the /welcome tracker runs on — shared react-query key, so this
  // costs nothing extra. While it says setup is unfinished the strip below owns
  // every setup prompt on this screen, and the site card stays out of its way.
  const { status: setupStatus } = useSetupStatus();
  const setupUnfinished = isRenderableSetupStatus(setupStatus) && !isSetupComplete(setupStatus);

  // Fetch recent reviews
  const { data: recentReviews } = useQuery({
    queryKey: ['reviews-recent'],
    queryFn: async () => {
      const response = await axios.get('/api/reviews?limit=3&sort=-reviewDate');
      return (response.data?.data?.reviews || []) as Review[];
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Fetch recent leads
  const { data: recentLeads } = useQuery({
    queryKey: ['leads-recent'],
    queryFn: async () => {
      const response = await axios.get('/api/leads?limit=3&sort=-createdAt');
      return (response.data?.data?.leads || []).map((l: any) => ({
        ...l,
        service: l.service || l.source,
      })) as Lead[];
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Fetch visitor count for the first active website
  const activeWebsiteId = websites?.find(w => w.status === 'active' && w.contentSetupComplete)?.id;
  const { data: visitorData } = useQuery({
    queryKey: ['dashboard-visitors', activeWebsiteId],
    queryFn: async () => {
      const response = await axios.get(`/api/websites/${activeWebsiteId}/analytics?period=30d`);
      return response.data?.data?.websiteStats as { visitors: { thisWeek: number; thisMonth: number } } | undefined;
    },
    enabled: !!activeWebsiteId,
    staleTime: 120000,
    retry: false,
  });

  /**
   * `user.name` is the business name on every pipeline-created account — the
   * invite sets it that way — so slicing a first name off it greeted people as
   * "Good morning, Noble". Accounts claimed since store the real contact name,
   * but the older ones never will; "there" is the honest fallback.
   */
  const firstName = user?.displayName ?? (personalFirstName(user?.name) || 'there');

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const handlePullRefresh = useCallback(() => refetch(), [refetch]);
  const pullToRefresh = usePullToRefresh(handlePullRefresh);

  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const handleDownloadReport = useCallback(async (site: Website) => {
    if (isGeneratingReport) return;
    setIsGeneratingReport(true);

    try {
      const now = new Date();
      const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

      // Fetch analytics data for the website
      let analyticsData: ReportAnalytics | null = null;
      try {
        const resp = await axios.get(`/api/websites/${site.id}/analytics`);
        analyticsData = (resp.data?.data?.websiteStats || null) as ReportAnalytics | null;
      } catch {
        // Analytics may not be available — continue without it
      }

      // Fetch leads
      let leads: ReportLead[] = [];
      try {
        const resp = await axios.get('/api/leads?limit=15&sort=-createdAt');
        leads = (resp.data?.data?.leads || []) as ReportLead[];
      } catch {
        // Continue without leads
      }

      // Fetch reviews
      let reviews: ReportReview[] = [];
      try {
        const resp = await axios.get('/api/reviews?limit=10&sort=-reviewDate');
        reviews = (resp.data?.data?.reviews || []) as ReportReview[];
      } catch {
        // Continue without reviews
      }

      generateReport(
        {
          businessName: site.businessName,
          domain: site.domain,
          trade: site.trade,
          leadsCount: site.leadsCount,
          newLeadsCount: site.newLeadsCount,
          reviewsCount: site.reviewsCount,
          avgRating: site.avgRating,
        },
        analyticsData,
        leads,
        reviews,
        month,
      );
    } catch (err) {
      console.error('Failed to generate report:', err);
    } finally {
      setIsGeneratingReport(false);
    }
  }, [isGeneratingReport]);

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <motion.div
        variants={pageVariants}
        initial="initial"
        animate="animate"
        className="bg-red-50 border border-red-100 rounded-xl p-6"
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-red-900">Error loading websites</h3>
            <p className="text-sm text-red-700 mt-1">Unable to load your websites. Please try again.</p>
            <button
              onClick={() => refetch()}
              className="mt-3 text-sm font-medium text-red-700 hover:text-red-800"
            >
              Try again
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  const totalLeads = websites?.reduce((sum, w) => sum + (w.leadsCount || 0), 0) || 0;
  const totalNewLeads = websites?.reduce((sum, w) => sum + (w.newLeadsCount || 0), 0) || 0;
  const totalReviews = websites?.reduce((sum, w) => sum + (w.reviewsCount || 0), 0) || 0;
  const ratedWebsites = websites?.filter(w => w.avgRating) || [];
  const avgRating = ratedWebsites.length > 0
    ? ratedWebsites.reduce((sum, w) => sum + (w.avgRating || 0), 0) / ratedWebsites.length
    : 0;
  const pendingResponses = recentReviews?.filter(r => !r.responded).length ?? 0;
  const websitesNeedingSetup = websites?.filter(w => !w.contentSetupComplete).length || 0;

  const singleSite = isSingleWebsite ? websites[0] : null;

  // Single Website Dashboard View
  if (isSingleWebsite && singleSite) {
    return (
      <div className="space-y-6" ref={pullToRefresh.containerRef}>
        <PullToRefreshIndicator
          isPulling={pullToRefresh.isPulling}
          isRefreshing={pullToRefresh.isRefreshing}
          pullDistance={pullToRefresh.pullDistance}
          pullThreshold={pullToRefresh.pullThreshold}
        />
        {/* Header */}
        <PageHeader
          eyebrow={`${getGreeting()}, ${firstName}`}
          title="Here's how your website is doing"
          actions={
            <>
              <Pill tone="outline" size="sm" onClick={() => refetch()}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                Refresh
              </Pill>
              <Pill
                tone="solid"
                size="sm"
                onClick={() => handleDownloadReport(singleSite)}
                disabled={isGeneratingReport}
              >
                {isGeneratingReport ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <DownloadIcon className="w-4 h-4" />
                )}
                {isGeneratingReport ? 'Generating…' : 'Download report'}
              </Pill>
            </>
          }
        />

        {/* Setup, compressed. Disappears the moment the site is live on the
            client's own domain — see SetupProgressStrip. */}
        <SetupProgressStrip />

        {/* The site itself — the capture is the only saturated colour here. */}
        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
          <div className="flex flex-col lg:flex-row">
            <SitePreviewFrame
              domain={singleSite.domain}
              screenshot={singleSite.screenshot}
              alt={singleSite.businessName || singleSite.domain}
              liveUrl={singleSite.currentActiveUrl || `https://${singleSite.domain}`}
              onRefreshShot={async () => {
                try {
                  await axios.post(`/api/websites/${singleSite.id}/screenshot/refresh`);
                  await queryClient.invalidateQueries({ queryKey: ['websites'] });
                } catch { /* silent */ }
              }}
            />

            <div className="flex flex-col lg:w-[56%]">
              <div className="flex items-start justify-between gap-4 border-b border-zinc-200 p-6 lg:p-7">
                <div className="min-w-0">
                  <h2 className="font-display text-2xl font-bold tracking-[-0.03em] text-primary">
                    {singleSite.businessName || singleSite.domain}
                  </h2>
                  <a
                    href={singleSite.currentActiveUrl || `https://${singleSite.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1.5 font-mono text-[13px] text-zinc-600 underline-offset-4 hover:text-primary hover:underline"
                  >
                    {singleSite.domain}
                    <ExternalLinkIcon className="w-3.5 h-3.5" />
                  </a>
                  {/* The strip above is already asking for the domain; a second
                      link for the same job is just the prompt twice. */}
                  {!isDemo && !setupUnfinished && singleSite.websiteStatus !== 'live' && (
                    <Link
                      to="/dashboard/account/domain"
                      className="mt-2 flex items-center gap-1.5 text-[13px] text-muted underline-offset-4 transition-colors hover:text-primary hover:underline"
                    >
                      <GlobeIcon className="w-4 h-4" />
                      Connect a custom domain
                    </Link>
                  )}
                </div>
                {setupUnfinished && setupStatus ? (
                  // Name the actual stage rather than a flat "Setup required" —
                  // the strip above says the same thing, in the same words.
                  <Tag tone="warn" dot>
                    {STAGE_DEFS[setupStatus.stage].label}
                  </Tag>
                ) : (
                  <Tag tone={singleSite.contentSetupComplete ? 'live' : 'warn'} dot>
                    {singleSite.contentSetupComplete ? 'Live' : 'Setup required'}
                  </Tag>
                )}
              </div>

              {/* The four numbers, hairline-separated rather than boxed */}
              <div className="grid grid-cols-2 divide-x divide-y divide-zinc-200 border-b border-zinc-200 sm:grid-cols-4 sm:divide-y-0">
                <Link to="/dashboard/leads" className="p-5 transition-colors hover:bg-paper-tint">
                  <Eyebrow>Leads</Eyebrow>
                  <p className="mt-2 font-display text-2xl font-bold tracking-[-0.03em] text-primary">
                    <SimpleCounter value={singleSite.leadsCount || 0} />
                  </p>
                  <p className="mt-1 font-mono text-[11px] text-zinc-500">
                    {(singleSite.newLeadsCount || 0) > 0 ? `+${singleSite.newLeadsCount} new` : 'no new'}
                  </p>
                </Link>
                <Link to="/dashboard/reviews" className="p-5 transition-colors hover:bg-paper-tint">
                  <Eyebrow>Rating</Eyebrow>
                  <p className="mt-2 font-display text-2xl font-bold tracking-[-0.03em] text-primary">
                    {singleSite.avgRating ? <RatingCounter value={singleSite.avgRating} /> : '—'}
                  </p>
                  <p className="mt-1 font-mono text-[11px] text-zinc-500">
                    {singleSite.reviewsCount || 0} reviews
                  </p>
                </Link>
                <Link to="/dashboard/analytics" className="p-5 transition-colors hover:bg-paper-tint">
                  <Eyebrow>Visitors</Eyebrow>
                  <p className="mt-2 font-display text-2xl font-bold tracking-[-0.03em] text-primary">
                    {visitorData?.visitors ? <SimpleCounter value={visitorData.visitors.thisWeek} /> : '—'}
                  </p>
                  <p className="mt-1 font-mono text-[11px] text-zinc-500">this week</p>
                </Link>
                <div className="p-5">
                  <Eyebrow>Uptime</Eyebrow>
                  <p className="mt-2 font-display text-2xl font-bold tracking-[-0.03em] text-primary">
                    {singleSite.websiteStatus === 'live' ? '100%' : '—'}
                  </p>
                  <p className="mt-1 font-mono text-[11px] text-zinc-500">last 30 days</p>
                </div>
              </div>

              {/* One action. Leads, reviews and analytics are a click away from
                  the numbers above and from the sidebar — five pills repeated
                  the same navigation three times. */}
              {/* One action. While setup is running the strip above carries the
                  "what's next" — this card only offers the editor, so the two
                  never point somewhere different. */}
              <div className="mt-auto flex flex-wrap items-center justify-between gap-3 p-6 lg:p-7">
                {singleSite.contentSetupComplete || setupUnfinished ? (
                  <>
                    {isDemo ? (
                      <Pill size="sm" tone="outline" disabled title="Available after you claim your site">
                        <PencilIcon />
                        Edit website
                      </Pill>
                    ) : (
                      <Pill to={`/dashboard/websites/${singleSite.id}/edit`} size="sm">
                        <PencilIcon />
                        Edit website
                      </Pill>
                    )}
                    {formatUpdated(singleSite.lastUpdated) && (
                      <p className="font-mono text-[11px] text-zinc-500">
                        Updated {formatUpdated(singleSite.lastUpdated)}
                      </p>
                    )}
                  </>
                ) : isDemo ? (
                  <Pill tone="outline" disabled title="Available after you claim your site">
                    Complete setup
                  </Pill>
                ) : (
                  <Pill to={`/dashboard/websites/${singleSite.id}/setup`} arrow>
                    Complete setup
                  </Pill>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Items */}
        {(totalNewLeads > 0 || pendingResponses > 0) && (
          <div>
            <Eyebrow className="mb-3">Needs you</Eyebrow>
            <HairlineGrid cols="sm:grid-cols-2">
              {totalNewLeads > 0 && (
                <Link
                  to="/dashboard/leads?status=new"
                  className="group flex items-center gap-4 bg-white p-5 transition-colors hover:bg-paper-tint"
                >
                  <span className="font-display text-3xl font-bold tracking-[-0.03em] text-primary">
                    {totalNewLeads}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-primary">
                      new lead{totalNewLeads > 1 ? 's' : ''}
                    </p>
                    <p className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                      need follow-up
                    </p>
                  </div>
                  <span className="text-zinc-400 transition-transform group-hover:translate-x-0.5" aria-hidden>→</span>
                </Link>
              )}
              {pendingResponses > 0 && (
                <Link
                  to="/dashboard/reviews?responded=false"
                  className="group flex items-center gap-4 bg-white p-5 transition-colors hover:bg-paper-tint"
                >
                  <span className="font-display text-3xl font-bold tracking-[-0.03em] text-primary">
                    {pendingResponses}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-primary">
                      review{pendingResponses > 1 ? 's' : ''}
                    </p>
                    <p className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
                      need a response
                    </p>
                  </div>
                  <span className="text-zinc-400 transition-transform group-hover:translate-x-0.5" aria-hidden>→</span>
                </Link>
              )}
            </HairlineGrid>
          </div>
        )}

        {/* Recent Activity */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Recent Leads */}
          <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
            <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
              <Eyebrow>Recent leads</Eyebrow>
              <Link
                to="/dashboard/leads"
                className="text-[13px] font-medium text-primary underline-offset-4 hover:underline"
              >
                View all
              </Link>
            </div>
            {recentLeads && recentLeads.length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {recentLeads.slice(0, 3).map((lead) => {
                  const tone: TagTone =
                    lead.status?.toLowerCase() === 'new'
                      ? 'live'
                      : lead.status?.toLowerCase() === 'qualified'
                        ? 'info'
                        : 'neutral';
                  return (
                    <div key={lead.id} className="flex items-center gap-3 px-5 py-3.5">
                      <LeadInitialAvatar name={lead.name} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-primary">{lead.name}</p>
                        <p className="truncate text-[13px] text-muted">
                          {lead.service || 'General inquiry'}
                        </p>
                      </div>
                      <Tag tone={tone}>{lead.status || 'New'}</Tag>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="px-5 py-8 text-center text-[13px] text-muted">No recent leads</p>
            )}
          </div>

          {/* Recent Reviews */}
          <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
            <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
              <Eyebrow>Recent reviews</Eyebrow>
              <Link
                to="/dashboard/reviews"
                className="text-[13px] font-medium text-primary underline-offset-4 hover:underline"
              >
                View all
              </Link>
            </div>
            {recentReviews && recentReviews.length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {recentReviews.slice(0, 3).map((review) => (
                  <div key={review.id} className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <svg
                            key={star}
                            className={`w-3.5 h-3.5 ${star <= review.rating ? 'text-primary fill-current' : 'text-zinc-200 fill-current'}`}
                            viewBox="0 0 24 24"
                          >
                            <path d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
                          </svg>
                        ))}
                      </div>
                      <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-zinc-500">
                        {review.platform}
                      </span>
                    </div>
                    <p className="mt-2 line-clamp-2 text-[14px] leading-relaxed text-primary">
                      &ldquo;{review.reviewText}&rdquo;
                    </p>
                    <p className="mt-1.5 font-mono text-[11px] text-zinc-500">
                      {review.reviewerName}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="px-5 py-8 text-center text-[13px] text-muted">No recent reviews</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Multi-website dashboard
  return (
    <div className="space-y-8" ref={pullToRefresh.containerRef}>
      <PullToRefreshIndicator
        isPulling={pullToRefresh.isPulling}
        isRefreshing={pullToRefresh.isRefreshing}
        pullDistance={pullToRefresh.pullDistance}
        pullThreshold={pullToRefresh.pullThreshold}
      />
      {/* Welcome Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-primary">
            {getGreeting()}, {firstName}
          </h1>
          <p className="text-muted mt-1">
            {websites && websites.length > 0
              ? `You have ${websites.length} website${websites.length > 1 ? 's' : ''} in your account`
              : 'Manage and monitor your websites'
            }
          </p>
        </div>
        {websites && websites.length > 0 && (
          <div className="flex items-center gap-2">
            {selectedWebsite && (
              <motion.button
                onClick={() => handleDownloadReport(selectedWebsite)}
                disabled={isGeneratingReport}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-ink-soft rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                whileHover={isGeneratingReport ? {} : { scale: 1.02 }}
                whileTap={isGeneratingReport ? {} : { scale: 0.98 }}
              >
                {isGeneratingReport ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <DownloadIcon className="w-4 h-4" />
                )}
                {isGeneratingReport ? 'Generating...' : 'Download Report'}
              </motion.button>
            )}
            <motion.button
              onClick={() => refetch()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted hover:text-primary bg-white border border-zinc-200 hover:border-zinc-300 rounded-lg transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
              Refresh
            </motion.button>
          </div>
        )}
      </div>

      {/* Dashboard Summary */}
      {websites && websites.length > 0 && (
        <motion.div
          className="bg-gradient-to-br from-primary to-primary/90 rounded-xl p-6 text-white"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Summary Stats with Animated Counters */}
            <div className="flex flex-wrap gap-6 lg:gap-10">
              <Link to="/dashboard/leads" className="group">
                <p className="text-3xl font-bold">
                  <SimpleCounter value={totalLeads} />
                </p>
                <p className="text-white/70 text-sm">Total Leads</p>
                {totalNewLeads > 0 && (
                  <span className="inline-flex items-center gap-1 mt-1 text-xs font-medium text-green-300">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                    {totalNewLeads} new
                  </span>
                )}
              </Link>
              <Link to="/dashboard/reviews" className="group">
                <p className="text-3xl font-bold">
                  {avgRating ? <RatingCounter value={avgRating} /> : '—'}
                </p>
                <p className="text-white/70 text-sm">Avg Rating</p>
                <span className="text-xs text-white/60">{totalReviews} reviews</span>
              </Link>
              <div>
                <p className="text-3xl font-bold">
                  <SimpleCounter value={websites.filter(w => w.status === 'active').length} />
                </p>
                <p className="text-white/70 text-sm">Live Websites</p>
              </div>
            </div>

            {/* Action Items */}
            <div className="flex flex-col gap-2">
              {totalNewLeads > 0 && (
                <motion.div whileHover={{ scale: 1.02 }}>
                  <Link
                    to="/dashboard/leads?status=new"
                    className="flex items-center gap-3 px-4 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                      <UsersIcon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{totalNewLeads} new lead{totalNewLeads > 1 ? 's' : ''}</p>
                      <p className="text-xs text-white/60">Need follow-up</p>
                    </div>
                    <svg className="w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </Link>
                </motion.div>
              )}
              {pendingResponses > 0 && (
                <motion.div whileHover={{ scale: 1.02 }}>
                  <Link
                    to="/dashboard/reviews?responded=false"
                    className="flex items-center gap-3 px-4 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <div className="w-8 h-8 bg-zinc-500 rounded-lg flex items-center justify-center">
                      <StarIcon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{pendingResponses} review{pendingResponses > 1 ? 's' : ''}</p>
                      <p className="text-xs text-white/60">Need response</p>
                    </div>
                    <svg className="w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </Link>
                </motion.div>
              )}
              {websitesNeedingSetup > 0 && (
                <div className="flex items-center gap-3 px-4 py-2.5 bg-white/10 rounded-lg">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                    <AlertIcon className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{websitesNeedingSetup} website{websitesNeedingSetup > 1 ? 's' : ''}</p>
                    <p className="text-xs text-white/60">Need setup</p>
                  </div>
                </div>
              )}
              {totalNewLeads === 0 && pendingResponses === 0 && websitesNeedingSetup === 0 && (
                <div className="flex items-center gap-3 px-4 py-2.5 bg-white/10 rounded-lg">
                  <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                    <CheckIcon className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">All caught up!</p>
                    <p className="text-xs text-white/60">No pending actions</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Per-Website Cards */}
      {websites && websites.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-primary mb-4">Your Websites</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {websites.map((site) => {
              const statusConfig: Record<string, { label: string; color: string }> = {
                active: { label: 'Active', color: 'bg-green-500' },
                setting_up: { label: 'Setting Up', color: 'bg-amber-500' },
                suspended: { label: 'Suspended', color: 'bg-red-500' },
              };
              const st = statusConfig[site.status] || statusConfig.setting_up;
              return (
                <motion.button
                  key={site.id}
                  onClick={() => { setSelectedWebsiteId(site.id); navigate('/dashboard'); }}
                  className="bg-white border border-zinc-200 rounded-xl p-4 text-left hover:border-accent/50 hover:shadow-md transition-all group"
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.15 }}
                >
                  {/* Screenshot */}
                  <div className="w-full aspect-[16/10] rounded-lg overflow-hidden bg-zinc-100 mb-3">
                    {site.screenshot ? (
                      <img src={site.screenshot} alt={site.businessName || site.domain} className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-400">
                        <GlobeIcon className="w-8 h-8" />
                      </div>
                    )}
                  </div>

                  {/* Name + status */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-primary truncate group-hover:text-accent transition-colors">
                        {site.businessName || site.domain}
                      </p>
                      <p className="text-xs text-muted truncate">{site.domain}</p>
                    </div>
                    <span className="flex items-center gap-1.5 text-xs font-medium text-muted flex-shrink-0">
                      <span className={`w-2 h-2 rounded-full ${st.color}`} />
                      {st.label}
                    </span>
                  </div>

                  {/* Stats row */}
                  <div className="flex items-center gap-4 text-xs text-muted mt-3 pt-3 border-t border-zinc-100">
                    <span className="flex items-center gap-1">
                      <UsersIcon className="w-3.5 h-3.5" />
                      {site.leadsCount || 0} leads
                    </span>
                    <span className="flex items-center gap-1">
                      <StarIcon className="w-3.5 h-3.5" />
                      {site.reviewsCount || 0} reviews
                    </span>
                    {(site.avgRating ?? 0) > 0 && (
                      <span className="flex items-center gap-1">
                        {site.avgRating?.toFixed(1)}
                      </span>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Activity Section */}
      {websites && websites.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Leads */}
          <motion.div
            className="bg-white border border-zinc-200 rounded-xl p-6"
            whileHover={{ y: -2 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-primary">Recent Leads</h2>
              <Link to="/dashboard/leads" className="text-sm text-primary underline-offset-4 hover:underline font-medium">
                View all
              </Link>
            </div>
            <div className="space-y-3">
              {recentLeads && recentLeads.length > 0 ? (
                recentLeads.slice(0, 3).map((lead) => {
                  const statusColors: Record<string, string> = {
                    new: 'bg-green-100 text-green-700',
                    contacted: 'bg-blue-100 text-blue-700',
                    qualified: 'bg-purple-100 text-purple-700',
                  };
                  return (
                    <div key={lead.id} className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                      <LeadInitialAvatar name={lead.name} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-primary">{lead.name}</p>
                        <p className="text-xs text-muted">{lead.service || 'General inquiry'}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[lead.status?.toLowerCase()] || statusColors.new}`}>
                        {lead.status || 'New'}
                      </span>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-muted text-center py-4">No recent leads</p>
              )}
            </div>
          </motion.div>

          {/* Recent Reviews */}
          <motion.div
            className="bg-white border border-zinc-200 rounded-xl p-6"
            whileHover={{ y: -2 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-primary">Recent Reviews</h2>
              <Link to="/dashboard/reviews" className="text-sm text-primary underline-offset-4 hover:underline font-medium">
                View all
              </Link>
            </div>
            <div className="space-y-3">
              {recentReviews && recentReviews.length > 0 ? (
                recentReviews.slice(0, 3).map((review) => (
                  <div key={review.id} className="p-3 bg-zinc-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex">
                        {[1,2,3,4,5].map((star) => (
                          <svg key={star} className={`w-4 h-4 ${star <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-zinc-300 fill-zinc-300'}`} viewBox="0 0 24 24">
                            <path d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
                          </svg>
                        ))}
                      </div>
                      <span className="text-xs text-muted capitalize">{review.platform}</span>
                    </div>
                    <p className="text-sm text-primary line-clamp-2">"{review.reviewText}"</p>
                    <p className="text-xs text-muted mt-1">{review.reviewerName}</p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted text-center py-4">No recent reviews</p>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Empty state when no websites */}
      {(!websites || websites.length === 0) && (
        <Empty
          framed
          mark={MARKS.websites}
          eyebrow="Welcome to Foundlio"
          title="Your dashboard fills up once your site is live"
          description="Traffic, calls, leads and reviews all land here — there's nothing to see until the first site is up."
          action={
            <motion.a
              href="https://foundlio.com/contact"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-white transition-transform"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Get Started
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </motion.a>
          }
        />
      )}
    </div>
  );
};

// Icons
const ExternalLinkIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
  </svg>
);

const DownloadIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
  </svg>
);

const GlobeIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.5 12h17M12 3.5c2 2.5 3.5 5.2 3.5 8.5s-1.5 6-3.5 8.5c-2-2.5-3.5-5.2-3.5-8.5s1.5-6 3.5-8.5z" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const AlertIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
  </svg>
);

const PencilIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
  </svg>
);

const LockIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
  </svg>
);

const UsersIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
  </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
  </svg>
);

// Quick Action Button Component with animation
interface QuickActionButtonProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  to: string;
  color: 'accent' | 'blue' | 'amber' | 'green';
}

const QuickActionButton: React.FC<QuickActionButtonProps> = ({ icon, label, description, to, color }) => {
  const colorClasses = {
    accent: 'bg-accent/10 text-accent hover:bg-accent/20',
    blue: 'bg-blue-50 text-blue-600 hover:bg-blue-100',
    amber: 'bg-zinc-50 text-zinc-600 hover:bg-zinc-100',
    green: 'bg-green-50 text-green-600 hover:bg-green-100',
  };

  return (
    <motion.div whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.98 }}>
      <Link
        to={to}
        className={`flex items-center gap-3 p-4 rounded-xl transition-colors ${colorClasses[color]}`}
      >
        <div className="flex-shrink-0">
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{label}</p>
          <p className="text-xs opacity-70 truncate">{description}</p>
        </div>
      </Link>
    </motion.div>
  );
};

export default DashboardHome;
